import {
    AttributeType,
    AttributeValue,
    PropsAttribute,
} from '../components/ui-element.type';
import { resolvePropertyDescriptor } from '../components/ui-element.helper';
import { nameConverter } from '../global/render-api';
import type { UIElement } from '../components/ui-element';
import type { GenericDecorator } from './decorator.type';
import {
    DefaultAttributeReflect,
    DefaultAttributeType,
} from '../components/ui-element.const';
import { isPlainObject } from '../global/helpers';
type AttributeDecoratorParam<T = HTMLElement> =
    | PropsAttribute<T>
    | AttributeType;

const isPropsAttribute = <T = HTMLElement>(
    params?: AttributeDecoratorParam<T>
): params is PropsAttribute<T> => isPlainObject(params);

const getInitialValueType = (value: AttributeValue): AttributeType =>
    typeof value as AttributeType;

export function Attribute(): GenericDecorator<void, ClassFieldDecoratorContext>;
export function Attribute(
    type: AttributeType
): GenericDecorator<void, ClassFieldDecoratorContext>;
export function Attribute(
    params: PropsAttribute
): GenericDecorator<void, ClassFieldDecoratorContext>;
export function Attribute<T>(
    params: PropsAttribute<T>
): GenericDecorator<void, ClassFieldDecoratorContext>;

/**
 * Defines an attribute for the component.
 * @param {AttributeDecoratorParam} [typeOrParams] - Optional attribute type or configuration object
 */
export function Attribute(
    typeOrParams?: AttributeDecoratorParam<HTMLElement>
): GenericDecorator<void, ClassFieldDecoratorContext> {
    return (_: void, context: ClassFieldDecoratorContext) => {
        if (context.kind === 'field') {
            context.addInitializer(function (this: unknown) {
                const instance = this as InstanceType<typeof UIElement>;
                const propertyName = String(context.name);
                const existingDescriptor = Object.getOwnPropertyDescriptor(
                    instance,
                    propertyName
                );
                const initialValue: AttributeValue =
                    existingDescriptor?.value as AttributeValue;
                let reflect: boolean = DefaultAttributeReflect;
                let adjustedType: AttributeType;

                const resolvedParams = typeOrParams;

                if (isPropsAttribute(resolvedParams)) {
                    adjustedType = resolvedParams.type ?? DefaultAttributeType;
                    reflect = resolvedParams?.reflect !== false;
                } else {
                    adjustedType =
                        resolvedParams ??
                        getInitialValueType(initialValue) ??
                        DefaultAttributeType;
                }
                const effectiveDefault =
                    isPropsAttribute(resolvedParams) &&
                    resolvedParams.default !== undefined
                        ? resolvedParams.default
                        : initialValue;
                const attributeName = nameConverter.propToAttribute(
                    isPropsAttribute(resolvedParams)
                        ? resolvedParams.name || propertyName
                        : propertyName
                );
                const descriptor = resolvePropertyDescriptor(
                    attributeName,
                    adjustedType,
                    effectiveDefault,
                    reflect
                );
                Object.defineProperty(instance, propertyName, descriptor);
            });
        } else {
            throw new Error(
                '@Attribute decorator is only applied to the property'
            );
        }
    };
}
